import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Smartphone, Wallet } from "lucide-react";
import { Button } from "../ui/button";
import { drawAllergyCard, type CardModel } from "../../features/allergy-card/drawCard";
import { exportAllergyCard } from "../../features/allergy-card/exportCard";
import { CARD_COPY, LANGUAGE_NAMES } from "../../features/allergy-card/catalog";
import type { AllergyUiCopy } from "../../features/allergy-card/copy";
import type { AllergyCardState } from "../../features/allergy-card/useAllergyCard";
import { securityUtils } from "../../lib/securityUtils";
import { usePostHog } from '../../analytics/usePostHog';

interface AllergyPreviewProps { state: AllergyCardState; copy: AllergyUiCopy }

export function AllergyPreview({ state, copy }: AllergyPreviewProps) {
  const analytics = usePostHog();
  const canvas = useRef<HTMLCanvasElement>(null);
  const container = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(350);
  const [format, setFormat] = useState<"phone" | "wallet">("phone");
  const [exportFormat, setExportFormat] = useState<"pdf" | "png">("pdf");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [rendering, setRendering] = useState(true);
  const [renderError, setRenderError] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  const model: CardModel = useMemo(() => ({
    ...state.pair, name: securityUtils.sanitizeInput(state.name), entries: state.entries, format, emptyLabel: copy.empty,
  }), [state.pair, state.name, state.entries, format, copy.empty]);

  useEffect(() => {
    if (!container.current) return;
    const observer = new ResizeObserver(([entry]) => setWidth(Math.round(entry.contentRect.width)));
    observer.observe(container.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    let cancelled = false;
    setRendering(true); setRenderError(false);
    // Draw offscreen first, so an older async render cannot overwrite newer input.
    const nextCanvas = document.createElement("canvas");
    drawAllergyCard(nextCanvas, model, width, pageIndex).then(result => {
      if (cancelled || !canvas.current) return;
      canvas.current.width = nextCanvas.width; canvas.current.height = nextCanvas.height;
      canvas.current.getContext("2d")?.drawImage(nextCanvas, 0, 0);
      setPageCount(result.pages.length); setPageIndex(result.pageIndex); setRendering(false);
    }).catch(() => { if (!cancelled) { setRenderError(true); setRendering(false); } });
    return () => { cancelled = true; };
  }, [model, width, pageIndex]);

  async function download() {
    const capture = analytics?.captureScope();
    capture?.('resource_download', { outcome: 'attempted', format: exportFormat });
    setExporting(true); setExportError(false);
    try {
      await exportAllergyCard(model, width, pageIndex, exportFormat);
      capture?.('resource_download', { outcome: 'generated', format: exportFormat });
    }
    catch { setExportError(true); capture?.('resource_download', { outcome: 'failed', format: exportFormat }); }
    finally { setExporting(false); }
  }

  return (
    <div className="flex min-w-0 flex-col border-t border-border bg-accent/40 p-4 sm:p-6 lg:border-l lg:border-t-0">
      <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">{copy.preview}</h2>
        <div className="flex rounded-lg border border-border bg-card p-1" role="group" aria-label={copy.preview}>
          <Button variant="ghost" size="sm" aria-pressed={format === "phone"} onClick={() => { setFormat("phone"); setPageIndex(0); }} className="h-10 gap-1.5 px-2.5 aria-pressed:bg-accent aria-pressed:text-accent-foreground"><Smartphone className="h-4 w-4" />{copy.phone}</Button>
          <Button variant="ghost" size="sm" aria-pressed={format === "wallet"} onClick={() => { setFormat("wallet"); setPageIndex(0); }} className="h-10 gap-1.5 px-2.5 aria-pressed:bg-accent aria-pressed:text-accent-foreground"><Wallet className="h-4 w-4" />{copy.wallet}</Button>
        </div>
      </div>
      <div ref={container} className="mx-auto w-full max-w-[350px]" data-allergy-card-preview data-page-count={pageCount}>
        {renderError ? <p role="alert" className="rounded-xl border border-border bg-card p-5 text-sm leading-6">{copy.layoutFailed}</p> : null}
        <canvas ref={canvas} width={1050} height={1866} role="img" aria-label={`${copy.card}: ${LANGUAGE_NAMES[state.pair.target]} / ${LANGUAGE_NAMES[state.pair.source]}`} aria-describedby="allergy-card-accessible-content" className={renderError ? "hidden" : "block h-auto w-full rounded-xl border border-border bg-white shadow-sm"} />
        <div id="allergy-card-accessible-content" className="sr-only">
          <p lang={state.pair.target}>{CARD_COPY[state.pair.target].title}</p>
          <ul>{state.entries.map(entry => <li key={entry.id}><span lang={state.pair.target}>{entry.primary}</span> / <span lang={state.pair.source}>{entry.secondary}</span></li>)}</ul>
          <p>{CARD_COPY[state.pair.source].doctor}</p>
        </div>
      </div>
      <p role="status" className="mt-4 text-center text-xs leading-5 text-muted-foreground">{rendering ? copy.preparing : pageCount > 1 ? `${pageCount} ${copy.pages}` : copy.fit}</p>
      {pageCount > 1 && <div role="group" aria-label={copy.preview} className="mt-2 flex items-center justify-center gap-4">
        <Button variant="outline" size="sm" className="h-11" disabled={pageIndex === 0} onClick={() => setPageIndex(previous => previous - 1)}>{copy.previous}</Button>
        <span className="text-sm tabular-nums" aria-live="polite">{pageIndex + 1} / {pageCount}</span>
        <Button variant="outline" size="sm" className="h-11" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(previous => previous + 1)}>{copy.next}</Button>
      </div>}
      <div className="mb-5 mt-3 text-center text-xs text-muted-foreground">{LANGUAGE_NAMES[state.pair.target]} / {LANGUAGE_NAMES[state.pair.source]}</div>
      <div className="mt-auto flex flex-wrap gap-2">
        <Button className="h-12 min-w-0 flex-1 gap-2" disabled={!state.canDownload || rendering || renderError || exporting} onClick={() => void download()}><Download className="h-4 w-4 shrink-0" />{exporting ? copy.downloading : copy.download}</Button>
        <select aria-label={copy.format} value={exportFormat} onChange={event => setExportFormat(event.target.value === "png" ? "png" : "pdf")} className="h-12 max-w-full rounded-lg border border-input bg-card px-2 text-xs">
          <option value="pdf">{copy.pdf}</option><option value="png">{copy.png}</option>
        </select>
      </div>
      {exportError && <p role="alert" className="mt-3 text-sm text-destructive">{copy.downloadFailed}</p>}
      <p className="mt-3 text-center text-xs leading-5 text-muted-foreground">{copy.previewNotice}</p>
    </div>
  );
}
